import isEqual from 'lodash/isEqual';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import {
	createStore,
	createActionsHook,
	createStateHook,
	createContainer,
} from '@atlassian/react-sweet-state';
import { expVal } from '@atlassian/jira-feature-experiments';
import { BULK_OPS_SELECTED_ISSUE_LIMIT } from '@atlassian/jira-bulk-operations-common/src/common/constants.tsx';
import { actions, initialState, type Actions } from './actions.tsx';
import type { SelectedIssuesState, IssueTableSelectedIssuesContainerProps } from './types.tsx';

export const IssueTableSelectedIssuesContainer =
	createContainer<IssueTableSelectedIssuesContainerProps>();

const Store = createStore<SelectedIssuesState, Actions, IssueTableSelectedIssuesContainerProps>({
	initialState,
	actions,
	name: 'jira-native-issue-table-selected-issues-state',
	containedBy: IssueTableSelectedIssuesContainer,
});

export const useIssueTableSelectedIssuesActions = createActionsHook<
	SelectedIssuesState,
	typeof actions
>(Store);

const getSelectedIssueIdsSelector = (state: SelectedIssuesState): string[] => {
	return Array.from(state.selectedIssueIds);
};

export const useIssueTableSelectedIssues = createStateHook(Store, {
	selector: getSelectedIssueIdsSelector,
});

const getSelectedState = (state: SelectedIssuesState): SelectedIssuesState => {
	return state;
};

export const useSelectedIssuesState = createStateHook(Store, {
	selector: getSelectedState,
});

const getSelectAllTotalSelected = (
	state: SelectedIssuesState,
	totalIssueSearchResultCount: number,
): number => {
	const limitedTotalIssueSearchResultCount =
		totalIssueSearchResultCount > BULK_OPS_SELECTED_ISSUE_LIMIT
			? BULK_OPS_SELECTED_ISSUE_LIMIT
			: totalIssueSearchResultCount;

	return (
		limitedTotalIssueSearchResultCount +
		state.selectedIssueIds.size -
		(limitedTotalIssueSearchResultCount >= BULK_OPS_SELECTED_ISSUE_LIMIT
			? state.exclusionCount
			: 0) -
		state.excludedIssueIds.size
	);
};

export const useIssueTableSelectAllTotalSelected = createStateHook(Store, {
	selector: getSelectAllTotalSelected,
});

const getIsIssueIdSelectedSelector = (
	state: SelectedIssuesState,
	{ rowIndex, issueId, depth }: { rowIndex: number; issueId: string; depth: number },
): boolean => {
	// issueId is a uuid when optimistically created via inline create and therefore NaN
	const isOptimisticIssueId =
		Number.isNaN(Number(issueId)) &&
		expVal('bulk_ops_select_all_beyond_experiment', 'isEnabled', false);
	if (isOptimisticIssueId) {
		return false;
	}
	const selectLimit = BULK_OPS_SELECTED_ISSUE_LIMIT - state.exclusionCount;

	if (
		state.isSelectingAll &&
		(rowIndex > selectLimit - 1 || state.excludedIssueIds.has(issueId)) &&
		!state.selectedIssueIds.has(issueId)
	) {
		return false;
	}

	return (state.isSelectingAll && !depth) || state.selectedIssueIds.has(issueId);
};

const getIsIssueIdSelectedSelectorOld = (state: SelectedIssuesState, issueId: string): boolean => {
	return state.selectedIssueIds.has(issueId);
};

// remove on clean up of bulk_ops_select_all_beyond_experiment
export const useIsIssueSelectedOld = createStateHook(Store, {
	selector: getIsIssueIdSelectedSelectorOld,
});

export const useIsIssueSelected = createStateHook(Store, {
	selector: getIsIssueIdSelectedSelector,
});

const getAreAllIssuesSelectedSelector = (
	state: SelectedIssuesState,
	issueIds: string[],
): boolean => {
	if (state.isSelectingAll && expVal('bulk_ops_select_all_beyond_experiment', 'isEnabled', false)) {
		// everything is selected unless there are issues in excludedIssueIds
		return state.excludedIssueIds.size === 0;
	}
	if (!issueIds) return false;
	return issueIds.length > 0 && issueIds.every((issueId) => state.selectedIssueIds.has(issueId));
};

export const useAreAllIssuesSelected = createStateHook(Store, {
	selector: getAreAllIssuesSelectedSelector,
});

const getIsHeaderCheckboxIndeterminate = (
	state: SelectedIssuesState,
	{
		isChecked,
		allUnloadedOrLoadedChilds,
	}: {
		isChecked: boolean;
		allUnloadedOrLoadedChilds: Set<string>;
	},
): boolean => {
	const { isSelectingAll, totalIssueSearchResultCount, excludedIssueIds, selectedIssueIds } = state;
	if (isSelectingAll) {
		const isOverSelectAllLimit =
			totalIssueSearchResultCount && totalIssueSearchResultCount >= BULK_OPS_SELECTED_ISSUE_LIMIT;
		const hasExcludedIssuesAndNotAllExcluded =
			excludedIssueIds.size > 0 && totalIssueSearchResultCount !== excludedIssueIds.size;
		const hasUnselectedExpandedChild = !isEqual(selectedIssueIds, allUnloadedOrLoadedChilds);

		return isOverSelectAllLimit || hasExcludedIssuesAndNotAllExcluded || hasUnselectedExpandedChild;
	}

	return !isChecked && selectedIssueIds.size > 0;
};

export const useIsHeaderCheckboxIndeterminate = createStateHook(Store, {
	selector: getIsHeaderCheckboxIndeterminate,
});

const getIsSelectingAll = (state: SelectedIssuesState): boolean => {
	return state.isSelectingAll;
};

export const useIsSelectingAll = createStateHook(Store, {
	selector: getIsSelectingAll,
});

const getExcludedIssueIds = (state: SelectedIssuesState): string[] => {
	return Array.from(state.excludedIssueIds);
};

export const useExcludedIssueIds = createStateHook(Store, {
	selector: getExcludedIssueIds,
});
